import AuthSubjectDialog from "@/components/AuthSubject";
import GdPermissionDialog, {
  GD_PERMISSION_TYPE,
  SetDownloadMemberInfo,
} from "@/components/GdPermissionDialog";
import HBMessage from "@/components/HBMessage";
import HBModalConfirm from "@/components/HBModalConfirm";
import LoginDialog from "@/components/LoginModal";
import MaterialDownloadDialog from "@/components/MaterialDownloadModal";
import { MutiSubjectTipModal } from "@/components/MaterialDownloadModal/MutiSubjectTipModal";
import MaterialFileTypeSelectModal from "@/components/MaterialFileTypeSelectModal";
import DownloadLimitModal from "@/components/PinDetail/DownloadLimitModal";
import { BuyVipDialog } from "@/components/Vip";
import { ORG_ROLE } from "@/constants/gaoding";
import { MATERIAL_SOURCE_TYPE, SOLUTION_VERSION } from "@/constants/gaodingVip";
import { LOCAL_STORAGE_KEY } from "@/constants/localStorageKey";
import { PermissionCodes } from "@/constants/permissionCodes";
import { getVipTypeById, isNoAuthByUser } from "@/constants/vip";
import useAppStore from "@/context/store";
import {
  getBizDownloadList,
  getBizUserRightList,
  getCertList,
  getCurrentVip,
  getDownloadUrl,
  getGdUserOrgs,
  getGdVipInfo,
  getGrocery,
  getHasDownloaded,
  getMeOrg,
  getPin,
  getPreDownloadInfo,
  VipCertStatusEnum,
} from "@/services";
import {
  isSelfOperatedTemplateMaterial,
  isThirdPartyMaterial,
  isThirdPartyTemplateMaterial,
} from "@/utils/materialUtils";
import { filter } from "lodash";
import { isGdMerged } from "./gaoding";
import {
  filterMaterailVip,
  isBigCompanyByGdVip,
  isNoAuthByGdVip,
} from "./gaodingVip";
import { hasPermission } from "./hooks/usePermisson";
import { gaodingEditHost } from "./host";
import { openUdesk } from "./initUdesk";

const getTypeNum = (category: string) => {
  switch (category) {
    case "图片":
      return 1;
    case "视频":
      return 3;
    case "平面模板":
      return 4;
    case "高端免抠":
      return 5;
    case "视频模板":
      return 6;
  }
};

const getDownloadFileName = (pin: IPin, file: IFile) => {
  let name = [
    "花瓣素材",
    // 限制文件名长度，避免文件名过长
    pin?.raw_text?.substring(0, 50) || new Date().getTime(),
    pin.material_info?.id,
  ].join("_");
  let ext = file?.file_format ? "." + file?.file_format : "";

  let filename = name + ext;

  // 非法字符替换（Windows下不允许以下字符：\/:*?"<>|，Mac OS下不允许以下字符：/）
  filename = filename.replace(/[/:*?"<>|]/g, "-");

  //判断文件名是否以空格结尾（不允许）
  filename = filename.replace(/\s+$/, "");

  return filename;
};

// 缓存grocery数据毕竟不会怎么改
let editOnlineLimitData = null;

/**
 * 依赖关系
 * pin -> pinId
 * files -> pin
 * file -> files、materialType
 * downloadUrl -> file
 */
export default class DownloadProcess {
  public pinId: string = "";
  public materialType: string = "";
  public downloadUrl: string = "";
  public user: IUser = null;
  public pin: IPin = null;
  public vips: any[] = null;
  public org: any = null;
  public file: IFile = null;
  public files: IFile[] = null;
  public certSubjects: CERTSubject[] = null;
  public lastCertSubject: CERTSubject = null;
  public userRights: IUserRight[] = null;
  public preDownloadInfo: any = null;
  public searchWord: string = "";
  public materialDownloadDialog: any = null;
  public materialId: string = "";
  public gdVip: IGdOrgVip["vip"] = null;
  // 是否是保存到画板
  public isSaveToBoard: boolean = false;
  public recordId: string = "";

  constructor(props) {
    this.pinId = props?.pinId || "";
    this.pin = props?.pin || null;
    this.materialType = props?.materialType || "";
    this.searchWord = props?.searchWord || "";
    this.file = props?.file || null;
    this.materialId = props?.materialId || "";
    this.isSaveToBoard = props?.isSaveToBoard || null;
    this.recordId = props?.recordId || "";
  }

  // 获取会员信息
  async getVips(fresh?: boolean) {
    if (fresh || !this.vips) {
      const result = await getCurrentVip();
      this.vips = result?.vips || [];
    }

    return this.vips;
  }

  async getGdVip(fresh?: boolean) {
    if (await this.checkMergeGdAccount()) {
      if (fresh || !this.gdVip) {
        const res = await getGdVipInfo({ embed: "pending_vips" });
        // 合并当前会员和待生效会员并过滤出素材相关会员
        this.gdVip = filterMaterailVip([
          ...(res?.vip || []),
          ...(res?.pending_vips || []),
        ]);
      }
      return this.gdVip;
    }
  }

  // 获取user信息
  async getUser(fresh?: boolean) {
    if (fresh) {
      this.user = await useAppStore.getState().refreshUser();
    } else if (!this.user) {
      this.user = useAppStore.getState().user;
    }

    return this.user;
  }

  // 获取编辑器限制数据（用于判断文件格式是否能调编辑器）
  async getEditOnlineLimitData() {
    if (!editOnlineLimitData) {
      editOnlineLimitData = await getGrocery("gaoding-editor-file-check");
    }

    return editOnlineLimitData;
  }

  // 获取租户信息
  async getOrg() {
    if (!this.org) {
      const result = await getMeOrg({
        org: true,
        org_role: true,
      });
      // 错误的话不继续走下去
      if (result?.err) {
        HBMessage.error("获取租户信息失败");
        throw new Error("获取租户信息失败");
      }
      this.org = result;
    }

    return this.org;
  }

  // 获取采集信息
  async getPin() {
    if (!this.pin) {
      const { pin, err, msg } = await getPin({
        pin_id: this.pinId,
        ignoreMessage: true,
      });

      if (err === 404) {
        HBMessage.error("素材已下架");
        throw new Error("素材已下架");
      }

      if (err || !pin) {
        HBMessage.error(msg || "素材获取失败");
        throw new Error("素材获取失败");
      }

      this.pin = pin;
    }

    return this.pin;
  }

  // 获取files信息
  // eslint-disable-next-line sonarjs/cognitive-complexity
  async getFiles() {
    if (!this.files) {
      const pin = await this.getPin();
      const info = pin.material_info;
      const material = pin.file_material;

      let files = [];

      if (
        material?.material_category === "平面模板" ||
        material?.material_category === "视频模板"
      ) {
        const file = info?.files?.find((file) => file.file_type === "ORIGIN");
        if (file) {
          files.push(file);
        }
      } else if (material?.material_category === "视频") {
        const mps = info?.content?.mps?.find((mps) => {
          return mps.name === "HD";
        });
        if (mps) {
          files.push({
            file_format: mps.file_format,
            file_size: mps.file_size,
            file_type: mps.name,
          });
        }
      } else {
        files = info?.files || [];
      }

      this.files = files;
    }

    return this.files;
  }

  // 获取单个file信息，可以通过文件格式materialType获取，或者默认取ORIGIN
  async getFile() {
    if (!this.file) {
      const files = await this.getFiles();
      if (this.materialType) {
        this.file =
          files?.find(
            (file) =>
              file?.file_format?.toLowerCase() ===
              this.materialType?.toLowerCase()
          ) || files[0];
      } else {
        if (files.length > 1) {
          this.file = files.find((file) => file.file_type === "ORIGIN");
        } else {
          this.file = files[0];
        }
      }

      if (!this.file) {
        HBMessage.error("获取file失败");
        throw new Error("获取file失败");
      }
    }

    return this.file;
  }

  // 获取文件下载链接
  async getDownloadUrl(ignoreError?: boolean) {
    if (!this.downloadUrl) {
      const pin = await this.getPin();
      const file = await this.getFile();
      let format = "";

      if (pin?.file_material?.material_category === "视频") {
        format = "HD";
      } else if (pin?.file_material?.material_category === "高端免抠") {
        format = file.file_type;
      }

      const result: any = await getDownloadUrl(
        {
          material_id: pin?.material_info?.id,
          download_type: 1,
          filename: getDownloadFileName(pin, file),
          ...(format ? { format } : null),
          type: getTypeNum(pin?.file_material?.material_category),
          // system_code: vips[0]?.system_code,
        },
        ignoreError
      );

      if (result?.err && !ignoreError) {
        HBMessage.error("获取下载链接失败");
        throw new Error("获取下载链接失败");
      }

      this.downloadUrl = result?.url || null;
    }

    return this.downloadUrl;
  }

  // 获取授权主体
  async getCertSubjects(fresh?: boolean) {
    if (fresh || !this.certSubjects) {
      const result: any = await getCertList({ valid: 1, page_size: 200 });
      if (result?.err) {
        HBMessage.error("获取授权主体列表失败");
        throw new Error("获取授权主体列表失败");
      }

      this.certSubjects = filter(
        result.list,
        // 融合二期会返回待生效状态的，需要将待生效的过滤掉
        (cert) => cert.cert_status_enum !== VipCertStatusEnum.WAIT_EFFECTIVE
      );
    }

    return this.certSubjects;
  }

  // 获取最近一次授权主体
  async getLastCertSubject() {
    if ((await this.checkHasAuth()) && !this.lastCertSubject) {
      const certSubjects = await this.getCertSubjects();
      const validCertSubjects = certSubjects.filter(
        (item) => item.subject_info?.id_no
      );
      this.lastCertSubject = validCertSubjects[0];
      if (validCertSubjects.length > 1) {
        // 通过最近下载记录获取最近使用的授权主体
        const result = await getBizDownloadList({ page_size: 1 });
        if (result.list?.length) {
          const id = result.list[0].vip_cert_subject_id;
          const item = validCertSubjects.find((item) => item.id === id);
          if (item) {
            this.lastCertSubject = item;
          }
        }
      }
    }

    return this.lastCertSubject;
  }

  // 获取预下载数据
  async getPreDownloadInfo(fresh?: boolean) {
    if (!this.preDownloadInfo || fresh) {
      const pin = await this.getPin();
      const lastCertSubject = await this.getLastCertSubject();
      const info = pin?.material_info;
      const material = pin?.file_material;
      // 调用预下载接口，判断是否可以下载
      const result = await getPreDownloadInfo({
        material_id: info?.id,
        download_type: 1,
        type: getTypeNum(material?.material_category),
        vip_cert_subject_ids: lastCertSubject && [lastCertSubject.id],
      });

      if (result.err) {
        HBMessage.error("当前素材无法被下载");
        throw new Error("当前素材无法被下载");
      }

      this.preDownloadInfo = result;
    }
    return this.preDownloadInfo;
  }

  // 获取用户权益列表
  async getUserRights(fresh?: boolean) {
    if (await this.checkMergeGdAccount()) {
      return [];
    }

    if (!this.userRights || fresh) {
      const result: any = await getBizUserRightList({
        page_size: 200,
        status: 1,
        not_expired: 1,
      });

      if (result.err) {
        HBMessage.error("获取用户权益失败");
        throw new Error("获取用户权益失败");
      }

      this.userRights = result.list;
    }

    return this.userRights;
  }

  // 获取搜索词
  getSearchWord() {
    return this.searchWord;
  }

  // 去稿定编辑器（通过稿定素材ID）
  async goGdEditorWithMaterialId() {
    const pin = await this.getPin();
    window.open(
      `https://${gaodingEditHost}/editor/redirect/${pin?.material_info?.id}?type=${pin?.material_info?.type}`
    );
  }

  // 去稿定编辑器（通过文件下载链接）
  async goGdEditorWithDownloadUrl() {
    const downloadUrl = await this.getDownloadUrl();
    window.open(
      `https://${gaodingEditHost}/editor/design?mode=open_remote_file&resource_url=${encodeURIComponent(
        downloadUrl
      )}&action_extension=autofocus`
    );
  }

  // 去合并稿定账号
  goMergeGdAccount() {
    const url = "/gdmerge";
    window.open(url);
  }

  goGaodingSpace() {
    window.open(`https://${gaodingEditHost}/team-space-upgrade`);
  }

  goGaodingDam() {
    window.open(
      `https://${gaodingEditHost}/dam/teams?import_file_id=${this.materialId}`
    );
  }

  // 判断是否登录
  async checkLogin() {
    return !!(await this.getUser());
  }

  // 判断是否是会员
  async checkVip() {
    if (await this.checkMergeGdAccount()) {
      const vips = await this.getGdVip();

      // 兼容大企业敏感素材问题，后续会优化删除
      await this.getVips();

      if (vips.some((item) => !item.is_expired && item.is_activied)) {
        return true;
      }
      // 大企业
      if (
        vips.some((item) => item.solution_version === SOLUTION_VERSION.FLAGSHIP)
      ) {
        HBModalConfirm.open({
          title: "提示",
          content: "您当前的企业版会员已过期，请联系客服续费",
          okText: "联系客服",
          cancelText: "我知道了",
          onOk: openUdesk,
        });
      }
    } else {
      return (await this.getVips())?.length > 0;
    }
  }

  // 判断是否是自产模板素材
  async checkProducedTemplateMaterial() {
    const pin = await this.getPin();
    return isSelfOperatedTemplateMaterial(pin?.material_info);
  }

  // 判断是否是第三方采购素材
  async checkProcurementMaterial() {
    const pin = await this.getPin();
    return isThirdPartyMaterial(pin?.material_info);
  }

  // 判断是否是第三方采购模板素材
  async checkProcurementTemplateMaterial() {
    const pin = await this.getPin();
    return isThirdPartyTemplateMaterial(pin?.material_info);
  }

  async checkHideEditOnline() {
    // 是否是第三方采购模板素材
    const isProcurementTemplateMaterial =
      await this.checkProcurementTemplateMaterial();
    const files = await this.getFiles();
    let filterFiles = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (await this.checkFileCanEditOnline(file)) {
        filterFiles.push(file);
      }
    }

    return (
      isProcurementTemplateMaterial ||
      files.length > 1 ||
      filterFiles.length === 0
    );
  }

  // 判断是否合并稿定账号
  async checkMergeGdAccount() {
    const user = await this.getUser();
    return isGdMerged(user);
  }

  // 判断租户是什么类型角色
  async checkGdOrgRole() {
    const org = await this.getOrg();
    if (org?.org_role?.roleEnum === ORG_ROLE.VISITOR) {
      // 如果是访客提示无权访问
      return GD_PERMISSION_TYPE.visitor;
    } else if (!org?.org) {
      // 租户被解散
      return GD_PERMISSION_TYPE.noorg;
    } else if (!org?.org_role?.roleEnum) {
      // 如果被移除了角色提示角色被移除
      return GD_PERMISSION_TYPE.remove;
    }
    return null;
  }

  // 判断非当前租户中是否有带花瓣会员的
  async checkOrgsHasVip() {
    const org = await this.getOrg();
    const orgs: any = await getGdUserOrgs();

    if (orgs.err) {
      HBMessage.error("获取租户列表失败");
      throw new Error("获取租户列表失败");
    }

    return (
      orgs.find(
        (item) => item.org_id === org?.org?.id?.value && !item?.hb_vip_name
      ) &&
      orgs?.some(
        (item) => !!item?.hb_vip_name && item.org_id !== org?.org?.id?.value
      )
    );
  }

  // 判断文件格式是否是稿定编辑器支持
  async checkFileCanEditOnline(_file?: IFile) {
    const file = _file || (await this.getFile());
    const item = (await this.getEditOnlineLimitData())?.list?.find((item) =>
      item.types.includes(file?.file_format?.toLowerCase())
    );
    return item && file?.file_size / (1024 * 1024) <= item.maxSize;
  }

  // 判断是否有商用授权
  async checkHasAuth() {
    const user = await this.getUser();
    if (await this.checkMergeGdAccount()) {
      const gdvip = await this.getGdVip();
      return !isNoAuthByGdVip(gdvip?.filter((item) => item.is_activied));
    } else {
      return !isNoAuthByUser(user);
    }
  }

  // 判断租户是否是管理员
  async checkOrgIsAdmin() {
    const org = await this.getOrg();
    return (
      org?.org_role?.roleEnum === ORG_ROLE.ADMIN ||
      org?.org_role?.roleEnum === ORG_ROLE.SUPER_ADMIN
    );
  }

  // 判断是否有有效授权主体
  async checkNoValidCertSubject() {
    const certSubjects = await this.getCertSubjects();
    const validCertSubjects = certSubjects.filter(
      (item) => item.subject_info?.id_no
    );

    return validCertSubjects.length === 0;
  }

  // 判断是否是多主体并且有主体未填写
  async checkMutiCertSubjectAndSomeNotFill() {
    const certSubjects = await this.getCertSubjects();
    const noInfoCertSubject = certSubjects.find(
      (item) => !item.subject_info?.id_no
    );
    return (
      certSubjects.length > 1 &&
      noInfoCertSubject &&
      !localStorage.getItem(LOCAL_STORAGE_KEY.HB_MUTI_SUBJECT_TIP)
    );
  }

  // 判断是否已下载该素材
  async checkHasDownloaded() {
    const result = await getHasDownloaded({ material_id: this.materialId });
    if (result.err) {
      HBMessage.error("获取下载状态失败");
      throw new Error("获取下载状态失败");
    }
    return !!result?.downloaded;
  }

  // 登录过程
  async processLogin(source_location?: string) {
    await new Promise<void>((resolve) => {
      LoginDialog.open({
        source_location: source_location,
        onOk: async () => {
          // 登录成功后，重新刷新权限信息
          await useAppStore.getState().refreshUserPermissions();
          resolve();
        },
      });
    });
  }

  // 文件格式选择过程
  async processFileSelect() {
    const pin = await this.getPin();
    const files = await this.getFiles();

    await new Promise<void>((resolve) => {
      MaterialFileTypeSelectModal.open({
        files,
        pin,
        onOk: (file) => {
          this.file = file;
          resolve();
        },
      });
    });
  }

  // 购买会员过程
  async processBuyVip({
    source,
    source_location,
    downloadClickCount,
  }: {
    source: string;
    source_location?: string;
    downloadClickCount?: number;
  }) {
    const searchWord = this.getSearchWord();

    const isThirdPartyMaterial = await this.checkProcurementMaterial();
    const materialSourceType = isThirdPartyMaterial
      ? MATERIAL_SOURCE_TYPE.THIRD
      : MATERIAL_SOURCE_TYPE.OFFICIAL;

    await new Promise<void>(async (resolve) => {
      BuyVipDialog.open({
        source,
        source_location,
        isCopyrightMaterial: 1,
        pinId: this.pin?.pin_id,
        searchWord,
        downloadClickCount,
        isGdDialog: true,
        materialSourceType,
        onFinish: async () => {
          await this.getVips(true);
          await this.getUser(true);
          if (await this.checkHasAuth()) {
            await this.getCertSubjects(true);
          }
          await useAppStore.getState().refreshUserPermissions();
          resolve();
        },
      });
    });
  }

  // 添加单个授权主体过程
  async processFillCertSubject() {
    const vips = isGdMerged(this.user)
      ? await this.getGdVip()
      : await this.getVips();
    const certSubjects = await this.getCertSubjects();
    await new Promise<void>((resolve) => {
      AuthSubjectDialog.open({
        vipId: vips[0].vip_id,
        certSubject: certSubjects[0],
        onOk: async () => {
          await this.getCertSubjects(true);
          await this.getUser(true);
          resolve();
        },
      });
    });
  }

  // 多主体提示过程
  async processMutiCertSubject() {
    const certSubjects = await this.getCertSubjects();
    try {
      await MutiSubjectTipModal.asyncOpen({
        subjects: certSubjects,
      });
    } catch (error) {}
  }

  // 处理没有下载权限场景的提示（单品类素材、自产素材等等）
  async processNoRightPackageModal() {
    let isBigCompany = false;
    if (await this.checkMergeGdAccount()) {
      const gdVips = await this.getGdVip();
      isBigCompany = isBigCompanyByGdVip(gdVips);
    } else {
      const vips = await this.getVips();
      isBigCompany = getVipTypeById(vips[0].vip_id) === "bigCompany";
    }

    DownloadLimitModal.open({
      isBigCompany,
      materialId: this.pin.extra.material_id,
    });
  }

  // 弹出下载弹窗
  async processDownloadModal({
    editOnline,
    onDownload,
    isSaveToBoard,
    recordId,
  }) {
    const pin = await this.getPin();
    const preDownloadInfo = await this.getPreDownloadInfo();
    const file = await this.getFile();
    const lastCertSubject = await this.getLastCertSubject();
    const searchWord = this.getSearchWord();
    const certSubjects = await this.getCertSubjects();
    const userRights = await this.getUserRights();
    const gdVip = await this.getGdVip();

    if (this.materialDownloadDialog) {
      this.materialDownloadDialog.destroy();
    }
    this.materialDownloadDialog = MaterialDownloadDialog.open({
      pin,
      info: preDownloadInfo,
      file,
      gdVip,
      searchWord,
      userRights,
      isSaveToBoard,
      recordId,
      subjects: certSubjects,
      defaultSelectSubject: lastCertSubject && [lastCertSubject.id],
      editOnline,
      onDownload: async () => {
        await this.getUserRights(true);
        onDownload && onDownload();
      },
    });
  }

  // 采集详情页素材在线编辑过程
  // eslint-disable-next-line sonarjs/cognitive-complexity
  async processPinDetailEditOnline(
    ignoreProcess?: boolean,
    source_location?: string
  ) {
    // 忽略一些流程，主要是给历史记录，权益过期的去使用
    if (!ignoreProcess) {
      // 判断是否登录
      if (!(await this.checkLogin())) {
        // 没登录去登录
        await this.processLogin("在线编辑");
      }

      // 判断是否合并稿定账号
      if (!(await this.checkMergeGdAccount())) {
        // 未合并跳合并页面
        this.goMergeGdAccount();
        return;
      }

      // 判断是否是自产模板素材
      if (await this.checkProducedTemplateMaterial()) {
        if (!hasPermission(PermissionCodes.templateEdit)) {
          GdPermissionDialog.open({
            type: GD_PERMISSION_TYPE.nopermission,
            operation: "在线编辑",
          });
          return;
        }

        // 自产模板素材直接通过稿定素材ID跳到编辑器
        this.goGdEditorWithMaterialId();
        return;
      }
    }

    // 判断租户类型是否是游客或者被移除
    const type = await this.checkGdOrgRole();
    if (type) {
      // 游客或被移除做提示
      GdPermissionDialog.open({
        type,
        operation: "在线编辑",
      });
      return;
    }

    // 判断是否是会员
    if (!(await this.checkVip())) {
      // 判断其他租户是否有会员
      if (await this.checkOrgsHasVip()) {
        GdPermissionDialog.open({
          type: GD_PERMISSION_TYPE.noright,
          operation: "在线编辑",
        });
        return;
      }
      // 非会员跳购买弹窗
      await this.processBuyVip({
        source: "在线编辑",
        source_location: source_location || "在线编辑",
      });
    }

    // 判断是否有商用版权
    if (await this.checkHasAuth()) {
      const certSubjects = await this.getCertSubjects();
      // 判断是否有授权主体
      if (!certSubjects?.length) {
        HBMessage.error("未获取到授权主体信息，请稍后再试");
        return;
      }
      // 判断是否有主体未填写
      if (await this.checkNoValidCertSubject()) {
        // 判断租户是否是管理员，只有管理员能填写，非管理员提示错误
        if (!(await this.checkOrgIsAdmin())) {
          HBMessage.error(
            "授权主体信息未填写，且您的身份无授权主体填写权限，请联系管理员"
          );
          return;
        }

        // 去填写授权主体信息
        await this.processFillCertSubject();
      }

      // 判断如果是多主体的话，是否有未填写的主体
      if (await this.checkMutiCertSubjectAndSomeNotFill()) {
        // 弹窗提示
        await this.processMutiCertSubject();
      }
    }
    // 获取预下载信息
    let preDownloadInfo = await this.getPreDownloadInfo(true);
    // 判断会员有没有，下载权益有没有
    if (
      preDownloadInfo.vips.length <= 0 ||
      preDownloadInfo.rights.length <= 0
    ) {
      // 购买会员
      await this.processBuyVip({
        source: "在线编辑",
        source_location: source_location || "在线编辑",
      });
      // 购买完会员需要全流程，基本上不会走到这步，因为前面已经判断是否是会员了
      this.processPinDetailEditOnline();
      return;
    }

    // 单品类会员是否有下载权益
    if (!preDownloadInfo.has_right_package) {
      // 单品类无下载权益提示
      this.processNoRightPackageModal();
      return;
    }

    if (!hasPermission(PermissionCodes.elementEdit)) {
      GdPermissionDialog.open({
        type: GD_PERMISSION_TYPE.nopermission,
        operation: "在线编辑",
      });
      return;
    }

    // 下载弹窗
    this.processDownloadModal({
      editOnline: true,
      onDownload: () => this.processPinDetailEditOnline(ignoreProcess),
      isSaveToBoard: this.isSaveToBoard,
      recordId: this.recordId,
    });
  }

  // 下载素材过程
  // eslint-disable-next-line sonarjs/cognitive-complexity
  async processDownloadMaterial(
    downloadClickCount?: number,
    source_location?: string
  ) {
    // 判断是否登录
    if (!(await this.checkLogin())) {
      // 没登录去登录
      await this.processLogin("素材下载");
    }

    // 判断是否是会员
    if (!(await this.checkVip())) {
      // 非会员跳购买弹窗
      await this.processBuyVip({
        source: "下载素材",
        source_location: source_location || "下载素材",
        downloadClickCount,
      });
    }

    // 判断是否合并稿定账号
    if (await this.checkMergeGdAccount()) {
      // 判断租户类型是否是游客或者被移除
      const type = await this.checkGdOrgRole();
      if (type) {
        // 游客或被移除做提示
        GdPermissionDialog.open({
          type,
          operation: "下载素材",
        });
        return;
      }

      // 判断是否当前用户是否有下载权限
      if (!hasPermission(PermissionCodes.materialDownload)) {
        // 有下载席位管理权限，提示去成员管理分配下载席位
        if (hasPermission(PermissionCodes.setModuleMember)) {
          SetDownloadMemberInfo.open();
          return;
        }

        GdPermissionDialog.open({
          type: GD_PERMISSION_TYPE.nopermission,
          operation: "下载素材",
        });
        return;
      }

      // 判断是否是会员以及其他租户是否有会员
      // if (!(await this.checkVip()) && (await this.checkOrgsHasVip())) {
      //   GdPermissionDialog.open({
      //     type: GD_PERMISSION_TYPE.noright,
      //     operation: "下载素材",
      //   });
      //   return;
      // }
    }

    // 判断是否有商用版权
    if (await this.checkHasAuth()) {
      const certSubjects = await this.getCertSubjects();
      // 判断是否有授权主体
      if (!certSubjects?.length) {
        HBMessage.error("未获取到授权主体信息，请稍后再试");
        return;
      }
      // 判断是否有主体未填写
      if (await this.checkNoValidCertSubject()) {
        // 判断是否合并稿定账号，如果是的话再判断租户是否是管理员，只有管理员能填写，非管理员提示错误
        if (
          (await this.checkMergeGdAccount()) &&
          !(await this.checkOrgIsAdmin())
        ) {
          HBMessage.error(
            "授权主体信息未填写，且您的身份无授权主体填写权限，请联系管理员"
          );
          return;
        }

        // 去填写主题
        await this.processFillCertSubject();
      }

      // 判断如果是多主体的话，是否有未填写的主体
      if (await this.checkMutiCertSubjectAndSomeNotFill()) {
        // 弹窗提示
        await this.processMutiCertSubject();
      }
    }

    // 获取预下载信息
    let preDownloadInfo = await this.getPreDownloadInfo(true);
    // 判断会员有没有，下载权益有没有
    if (
      preDownloadInfo.vips?.length <= 0 ||
      preDownloadInfo.rights?.length <= 0
    ) {
      // 购买会员
      await this.processBuyVip({
        source: "下载",
      });
      // 购买完会员需要全流程，基本上不会走到这步，因为前面已经判断是否是会员了
      this.processDownloadMaterial();
      return;
    }

    // 单品类会员是否有下载权益
    if (!preDownloadInfo.has_right_package) {
      // 单品类无下载权益提示
      this.processNoRightPackageModal();
      return;
    }

    // 下载弹窗
    this.processDownloadModal({
      editOnline: false,
      isSaveToBoard: this.isSaveToBoard,
      recordId: this.recordId,
      onDownload: () => this.processDownloadMaterial(),
    });
  }

  // 下载历史在线编辑过程
  async processDownloadHistoryEditOnline(source_location?: string) {
    // 判断是否登录
    if (!(await this.checkLogin())) {
      // 没登录去登录
      await this.processLogin("在线编辑");
    }

    // 判断是否是第三方采购素材
    if (await this.checkProcurementTemplateMaterial()) {
      HBMessage.error("当前素材暂不支持「在线编辑」，请选择其他素材");
      return;
    }

    // 判断是否合并稿定账号
    if (!(await this.checkMergeGdAccount())) {
      // 未合并跳合并页面
      this.goMergeGdAccount();
      return;
    }

    // 判断是否是自产模板素材
    if (await this.checkProducedTemplateMaterial()) {
      if (!hasPermission(PermissionCodes.templateEdit)) {
        GdPermissionDialog.open({
          type: GD_PERMISSION_TYPE.nopermission,
          operation: "在线编辑",
        });
        return;
      }
      // 自产模板素材直接通过稿定素材ID跳到编辑器
      this.goGdEditorWithMaterialId();
      return;
    }

    // 判断文件是否支持跳稿定编辑器
    if (await this.checkFileCanEditOnline()) {
      if (!hasPermission(PermissionCodes.elementEdit)) {
        GdPermissionDialog.open({
          type: GD_PERMISSION_TYPE.nopermission,
          operation: "在线编辑",
        });
        return;
      }

      // 判断是否是会员
      if (!(await this.checkVip())) {
        // 非会员跳购买弹窗
        await this.processBuyVip({
          source: "在线编辑",
          source_location: source_location || "下载历史-在线编辑",
        });
      }

      const url = await this.getDownloadUrl(true);
      if (url) {
        // 通过文件链接方式跳稿定编辑器
        this.goGdEditorWithDownloadUrl();
      } else {
        // 权益过期的话就走原有的编辑流程
        this.processPinDetailEditOnline(true);
      }
    } else {
      // 提示不支持
      HBModalConfirm.open({
        title: "编辑提示",
        content: "当前素材暂不支持「在线编辑」，请选择其他素材",
        okText: "知道了",
        tracker: {
          "data-dialog-title": "编辑格式限制",
          "data-gd-expose": "dialog_expose",
        },
      });
    }
  }

  // 分享引导去稿定空间
  async processShareToGaodingSpace() {
    // 判断是否登录
    if (!(await this.checkLogin())) {
      // 没登录去登录
      await this.processLogin("分享到稿定空间");
    }

    // 判断是否合并稿定账号
    if (!(await this.checkMergeGdAccount())) {
      // 未合并跳合并页面
      this.goMergeGdAccount();
      return;
    }

    this.goGaodingSpace();
  }

  async processBuyGaodouOrTemplate(href?: string) {
    // 判断是否登录
    if (!(await this.checkLogin())) {
      // 没登录去登录
      await this.processLogin("购买稿定会员");
    }

    if (href) {
      // 判断是否合并稿定账号
      if (!(await this.checkMergeGdAccount())) {
        // 未合并跳合并页面
        this.goMergeGdAccount();
        return;
      }

      window.open(href);
    } else {
      this.processBuyVip({
        source: "立即购买",
      });
    }
  }

  async processStorageToDam() {
    // 判断是否登录
    if (!(await this.checkLogin())) {
      // 没登录去登录
      await this.processLogin("存储到DAM");
    }

    // 判断是否合并稿定账号
    if (!(await this.checkMergeGdAccount())) {
      // 未合并跳合并页面
      this.goMergeGdAccount();
      return;
    }

    // 判断是否是会员
    if (!(await this.checkVip())) {
      // 非会员跳购买弹窗
      await this.processBuyVip({
        source: "下载",
      });
    }

    if (!hasPermission(PermissionCodes.materialimportDAM)) {
      GdPermissionDialog.open({
        type: GD_PERMISSION_TYPE.nopermission,
        operation: "导入DAM",
      });
      return;
    }

    // 判断是否有商用版权
    if (await this.checkHasAuth()) {
      const certSubjects = await this.getCertSubjects();
      // 判断是否有授权主体
      if (!certSubjects?.length) {
        HBMessage.error("未获取到授权主体信息，请稍后再试");
        return;
      }
      // 判断是否有主体未填写
      if (await this.checkNoValidCertSubject()) {
        // 判断是否合并稿定账号，如果是的话再判断租户是否是管理员，只有管理员能填写，非管理员提示错误
        if (
          (await this.checkMergeGdAccount()) &&
          !(await this.checkOrgIsAdmin())
        ) {
          HBMessage.error(
            "授权主体信息未填写，且您的身份无授权主体填写权限，请联系管理员"
          );
          return;
        }

        // 去填写主题
        await this.processFillCertSubject();
      }

      // 判断如果是多主体的话，是否有未填写的主体
      if (await this.checkMutiCertSubjectAndSomeNotFill()) {
        // 弹窗提示
        await this.processMutiCertSubject();
      }
    }

    if (await this.checkHasDownloaded()) {
      // 待判断是否有下载权益
      this.goGaodingDam();
    } else {
      // 获取预下载信息
      let preDownloadInfo = await this.getPreDownloadInfo(true);
      // 判断会员有没有，下载权益有没有
      if (
        preDownloadInfo.vips.length <= 0 ||
        preDownloadInfo.rights.length <= 0
      ) {
        // 购买会员
        await this.processBuyVip({
          source: "下载",
        });
        // 购买完会员需要全流程，基本上不会走到这步，因为前面已经判断是否是会员了
        this.processStorageToDam();
        return;
      }

      // 单品类会员是否有下载权益
      if (!preDownloadInfo.has_right_package) {
        // 单品类无下载权益提示
        this.processNoRightPackageModal();
        return;
      }

      // 下载弹窗
      this.processDownloadModal({
        editOnline: false,
        onDownload: () => this.processStorageToDam(),
        isSaveToBoard: this.isSaveToBoard,
        recordId: this.recordId,
      });
    }
  }
}
